import { Box, Checkbox, Stack, Typography, useMediaQuery } from "@mui/material";
import {
  RankingAnswerV1,
  RankingAnswerV1DTO,
} from "@pulse/pulse-rpcs/lib/types/rankingAnswerV1";
import { SurveySettingsEnums } from "@pulse/pulse-rpcs/lib/types/surveySettings";
import {
  EMPTY_JSON,
  RICH_TEXT_DEFAULT_FONT_COLOR,
  RICH_TEXT_MARKDOWN_PROPS,
  RankingQuestionV1Model,
  createRankingQuestionOptionsModel,
  createRankingQuestionV1Model,
  richTextTypography,
} from "@pulse/shared-components";
import {
  useCornerRadius,
  useFoundationColorTokens,
  useSpacing,
  useTypography,
} from "@surya-digital/leo-reactjs-material-ui";
import { RichTextEditor } from "@surya-digital/leo-reactjs-remirror";
import { observer } from "mobx-react";
import { Instance } from "mobx-state-tree";
import React, { useCallback, useEffect, useMemo } from "react";

const DESKTOP_BREAKPOINT = "(min-width:1200px)";

interface ChatRankingQuestionBodyProps {
  questionDetailsJson: string;
  draftAnswerJson?: string;
  fontFamily: string;
  fontScale: SurveySettingsEnums.FontSizeScale.FontSizeScale;
  areAllOptionsMandatoryText: string;
  maxSelectionText: string;
  isMandatory: boolean;
  onAnswerChange: (answerJson: string | null, isValid: boolean) => void;
  onResetReady: (reset: (() => void) | null) => void;
}

interface ChatRankingAnswerListProps {
  questionDetailsJson: string;
  answerJson: string;
}

const isAnswerPresent = (answerJson: string): boolean =>
  answerJson !== EMPTY_JSON;

export const createChatRankingQuestionModel = (
  questionDetailsJson: string,
  answerJson?: string,
): Instance<typeof RankingQuestionV1Model> => {
  const model = createRankingQuestionV1Model(undefined, [
    createRankingQuestionOptionsModel(),
  ]);
  model.deserializeQuestionJSON(questionDetailsJson);
  if (answerJson && isAnswerPresent(answerJson)) {
    const savedAnswer = JSON.parse(answerJson) as RankingAnswerV1DTO;
    model.deserializeAnswerJSON(
      JSON.stringify({
        ...savedAnswer,
        answers: savedAnswer.answers
          .filter((choice) =>
            model.options.some(
              (option) => option.code === choice.answer.code.code,
            ),
          )
          .map((choice) => ({
            ...choice,
            answer: {
              ...choice.answer,
              answer:
                model.options.find(
                  (option) => option.code === choice.answer.code.code,
                )?.option ?? choice.answer.answer,
            },
          })),
      }),
    );
  }
  return model;
};

const ChatRankingOptionText = ({
  text,
  fontFamily,
  fontScale,
  isBigScreen,
  color,
}: {
  text: string;
  fontFamily: string;
  fontScale: SurveySettingsEnums.FontSizeScale.FontSizeScale;
  isBigScreen: boolean;
  color: string;
}): React.ReactElement => (
  <Box
    sx={{
      color,
      minWidth: 0,
      "& .ProseMirror, & .ProseMirror *": {
        color: `${color} !important`,
        overflowWrap: "anywhere",
      },
    }}
  >
    <RichTextEditor
      name="chatRankingOption"
      initialValue={text}
      typography={richTextTypography(
        fontFamily,
        fontScale,
        RICH_TEXT_DEFAULT_FONT_COLOR,
        isBigScreen,
      )}
      mode="preview"
      supports={RICH_TEXT_MARKDOWN_PROPS}
      borderOnPreview={false}
    />
  </Box>
);

/**
 * Chat-specific ranking interaction. It uses the shared ranking model for its
 * ordering and validation rules, but retains the lighter chat-option treatment.
 */
export const ChatRankingQuestionBody = observer(
  ({
    questionDetailsJson,
    draftAnswerJson,
    fontFamily,
    fontScale,
    areAllOptionsMandatoryText,
    maxSelectionText,
    isMandatory,
    onAnswerChange,
    onResetReady,
  }: ChatRankingQuestionBodyProps): React.ReactElement => {
    const tokens = useFoundationColorTokens();
    const spacing = useSpacing();
    const radius = useCornerRadius();
    const typography = useTypography();
    const isBigScreen = useMediaQuery(DESKTOP_BREAKPOINT);
    const optionBorderColor = `var(--chat-group-option-border-color, ${tokens.border})`;
    const model = useMemo(
      () =>
        createChatRankingQuestionModel(questionDetailsJson, draftAnswerJson),
      [draftAnswerJson, questionDetailsJson],
    );

    const publishAnswer = useCallback((): void => {
      onAnswerChange(
        model.selectedOptions.length > 0 ? model.serializedAnswerJSON : null,
        model.validateUserResponse(isMandatory) === null,
      );
    }, [isMandatory, model, onAnswerChange]);

    const reset = useCallback((): void => {
      model.resetRanking();
      publishAnswer();
    }, [model, publishAnswer]);

    useEffect(() => {
      publishAnswer();
    }, [publishAnswer]);

    useEffect(() => {
      onResetReady(reset);
      return (): void => onResetReady(null);
    }, [onResetReady, reset]);

    const toggleOption = (option: { code: string; option: string }): void => {
      model.setSelectedOptions(option);
      publishAnswer();
    };

    const shouldShowHelperText =
      model.options.length !== model.maxSelection ||
      model.areAllOptionsMandatory;
    const helperText = model.areAllOptionsMandatory
      ? areAllOptionsMandatoryText
      : `${maxSelectionText} ${model.maxSelection}`.trim();

    return (
      <Stack gap={spacing.spaceXS} width="100%">
        {shouldShowHelperText && helperText && (
          <Typography sx={{ ...typography.b2, color: tokens.labelSubtle }}>
            {helperText}
          </Typography>
        )}
        <Stack gap={spacing.spaceSM} width="100%">
          {model.options.map((option) => {
            const selected = model.isChoiceSelected(option);
            return (
              <Stack
                key={option.code}
                component="div"
                role="checkbox"
                aria-checked={selected}
                tabIndex={0}
                direction="row"
                alignItems="center"
                gap={spacing.spaceXS}
                minHeight="44px"
                padding={`${spacing.spaceXS} ${spacing.spaceSM}`}
                bgcolor={
                  selected
                    ? tokens.backgroundPrimarySubtle
                    : tokens.backgroundElevatedLevel1
                }
                border={
                  selected
                    ? `2px solid ${tokens.borderPrimary}`
                    : `1px solid ${optionBorderColor}`
                }
                borderRadius={radius.radiusXS}
                textAlign="left"
                onClick={(): void => toggleOption(option)}
                onKeyDown={(event): void => {
                  if (event.key === " " || event.key === "Enter") {
                    event.preventDefault();
                    toggleOption(option);
                  }
                }}
                sx={{
                  cursor: "pointer",
                  font: "inherit",
                  "&:hover": {
                    backgroundColor: selected
                      ? tokens.backgroundPrimarySubtle
                      : tokens.backgroundHovered,
                  },
                }}
              >
                {selected && option.rank ? (
                  <Stack
                    alignItems="center"
                    justifyContent="center"
                    width="20px"
                    height="20px"
                    borderRadius="50%"
                    bgcolor={tokens.backgroundPrimary}
                    flexShrink={0}
                  >
                    <Typography
                      sx={{ ...typography.s2, color: tokens.labelOnSurface }}
                    >
                      {option.rank}
                    </Typography>
                  </Stack>
                ) : (
                  <Checkbox
                    checked={false}
                    disableRipple
                    inputProps={{ "aria-hidden": true, tabIndex: -1 }}
                    sx={{
                      color: tokens.iconPrimary,
                      padding: 0,
                      pointerEvents: "none",
                    }}
                  />
                )}
                <ChatRankingOptionText
                  text={option.option}
                  fontFamily={fontFamily}
                  fontScale={fontScale}
                  isBigScreen={isBigScreen}
                  color={selected ? tokens.labelPrimary : tokens.label}
                />
              </Stack>
            );
          })}
        </Stack>
      </Stack>
    );
  },
);

/** Renders completed ranking values in ascending rank order. */
export const ChatRankingAnswerList = ({
  questionDetailsJson,
  answerJson,
}: ChatRankingAnswerListProps): React.ReactElement => {
  const model = useMemo(
    () => createChatRankingQuestionModel(questionDetailsJson),
    [questionDetailsJson],
  );
  const answer = useMemo(
    () => RankingAnswerV1.fromDTO(JSON.parse(answerJson)),
    [answerJson],
  );

  return (
    <Box
      component="ol"
      sx={{ margin: 0, paddingInlineStart: "24px", overflowWrap: "anywhere" }}
    >
      {[...answer.answers]
        .sort((left, right) => left.rank - right.rank)
        .map((choice) => {
          const option = model.options.find(
            (configuredOption) =>
              configuredOption.code === choice.answer.code.code,
          );
          return (
            <Typography component="li" key={choice.answer.code.code}>
              {option?.option ??
                choice.answer.answer ??
                choice.answer.code.code}
            </Typography>
          );
        })}
    </Box>
  );
};
